import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, Plus, Pencil, ToggleLeft, ToggleRight, Building2, RefreshCw, Trash2, Zap } from 'lucide-react'
import BrandLogo from '../components/BrandLogo'
import LoanProductForm from '../components/LoanProductForm'
import FinancialInstitutionForm from '../components/FinancialInstitutionForm'
import { useAuth } from '../contexts/AuthContext'
import {
  getFinancialInstitutions,
  createFinancialInstitution,
  updateFinancialInstitution,
  toggleFinancialInstitutionStatus,
  deleteFinancialInstitution,
  getLoanProducts,
  createLoanProduct,
  updateLoanProduct,
  toggleLoanProductStatus,
  deleteLoanProduct,
  type FinancialInstitution,
  type LoanProduct,
  type LoanProductCreate,
  type FinancialInstitutionCreate,
} from '../api/adminApi'

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  variable: 'Variable',
  fixed_1yr: 'Fixed 1yr',
  fixed_2yr: 'Fixed 2yr',
  fixed_3yr: 'Fixed 3yr',
  fixed_4yr: 'Fixed 4yr',
  fixed_5yr: 'Fixed 5yr',
  split: 'Split',
}

export default function DataEntryPortal() {
  const navigate = useNavigate()
  const { logout, getAccessToken, user } = useAuth()

  const [institutions, setInstitutions] = useState<FinancialInstitution[]>([])
  const [products, setProducts] = useState<LoanProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Filters
  const [filterInstitution, setFilterInstitution] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all')

  // Modal state
  const [showProductForm, setShowProductForm] = useState(false)
  const [showFIForm, setShowFIForm] = useState(false)
  const [editingProduct, setEditingProduct] = useState<LoanProduct | null>(null)
  const [editingFI, setEditingFI] = useState<FinancialInstitution | null>(null)

  // Scraper state
  const [scrapeStatus, setScrapeStatus] = useState<'idle' | 'scraping' | 'done' | 'error'>('idle')
  const [scrapeResult, setScrapeResult] = useState('')
  const [lastScrapeTime, setLastScrapeTime] = useState<string | null>(null)
  const [scraperOnline, setScraperOnline] = useState<boolean | null>(null)
  const [availableScrapers, setAvailableScrapers] = useState<string[]>([])
  const [selectedScraper, setSelectedScraper] = useState('')
  const [useSampleData, setUseSampleData] = useState(true)
  const [saveToDatabase, setSaveToDatabase] = useState(false)
  const scrapeAbortRef = useRef<AbortController | null>(null)

  const SCRAPER_BASE = import.meta.env.VITE_DATA_SCRAPER_URL
    || (import.meta.env.DEV ? 'http://localhost:8001' : 'http://moneybridge-feature-data-scraper.australiaeast.azurecontainer.io:8001')

  const token = getAccessToken() ?? ''
  const isAdmin = user?.userType === 'admin'

  const loadData = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError('')
    try {
      const [fis, prods] = await Promise.all([
        getFinancialInstitutions(token),
        getLoanProducts(token),
      ])
      setInstitutions(fis)
      setProducts(prods)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { loadData() }, [loadData])

  // ── Scraper status on mount (with retry + periodic re-check) ─────────────
  useEffect(() => {
    const checkStatus = async (timeout = 10000) => {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), timeout)
        const res = await fetch(`${SCRAPER_BASE}/status`, { signal: controller.signal })
        clearTimeout(timeoutId)
        const data = await res.json()
        setScraperOnline(data.status === 'online')
        const scrapers = data.available_scrapers || []
        setAvailableScrapers(scrapers)
        if (scrapers.length > 0 && !selectedScraper) {
          setSelectedScraper(scrapers[0])
        }
        if (data.last_scrape?.timestamp) {
          setLastScrapeTime(data.last_scrape.timestamp)
        }
        return true
      } catch {
        return false
      }
    }

    const loadScraperStatus = async () => {
      // Try up to 3 times with increasing timeouts (handles Azure cold-start)
      for (const timeout of [10000, 15000, 20000]) {
        if (await checkStatus(timeout)) return
      }
      setScraperOnline(false)
    }

    loadScraperStatus()

    // Periodic re-check every 60s (recovers from transient failures)
    const interval = setInterval(() => checkStatus(10000), 60000)
    return () => clearInterval(interval)
  }, [SCRAPER_BASE])

  const handleTriggerScrape = async () => {
    const target = selectedScraper || (availableScrapers.length > 0 ? availableScrapers[0] : '')
    if (!target) {
      setScrapeStatus('error')
      setScrapeResult('No scrapers available')
      return
    }

    setScrapeStatus('scraping')
    setScrapeResult('')

    // Warm up scraper (fire-and-forget)
    try { fetch(`${SCRAPER_BASE}/status`).catch(() => { /* warm-up fire-and-forget */ }) } catch { /* ignored */ }

    try {
      const controller = new AbortController()
      scrapeAbortRef.current = controller
      const timeoutId = setTimeout(() => controller.abort(), 90000) // 90s timeout
      const response = await fetch(`${SCRAPER_BASE}/scrape/${target}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          institution_code: target,
          use_sample_data: useSampleData,
          save_to_database: saveToDatabase,
        }),
        signal: controller.signal,
      })
      clearTimeout(timeoutId)
      scrapeAbortRef.current = null

      const data = await response.json()
      if (data.success) {
        const count = data.products_count ?? data.products?.length ?? 0
        setScrapeStatus('done')
        setScrapeResult(`Scraped ${count} product${count !== 1 ? 's' : ''} from ${target.toUpperCase()}`)
        setLastScrapeTime(new Date().toISOString())
        // Reload products to show newly scraped data
        await loadData()
      } else {
        setScrapeStatus('error')
        setScrapeResult(data.error || data.message || 'Scrape returned unsuccessful')
      }
    } catch (err) {
      scrapeAbortRef.current = null
      if (err instanceof DOMException && err.name === 'AbortError') {
        setScrapeStatus('error')
        setScrapeResult('Scrape timed out (90s). The scraper may still be running.')
      } else {
        setScrapeStatus('error')
        setScrapeResult(err instanceof Error ? err.message : 'Scrape failed')
      }
    }
  }

  // ── Derived stats ───────────────────────────────────────────────────────────
  const activeProducts = products.filter((p) => p.is_active)
  const thisWeekActive = activeProducts.filter((p) => {
    if (!p.last_scraped_at) return false
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - 7)
    return new Date(p.last_scraped_at) >= cutoff
  })

  // ── Filtered products ───────────────────────────────────────────────────────
  const filteredProducts = products.filter((p) => {
    if (filterInstitution && p.institution_id !== filterInstitution) return false
    if (filterType && p.product_type !== filterType) return false
    if (filterActive === 'active' && !p.is_active) return false
    if (filterActive === 'inactive' && p.is_active) return false
    return true
  })

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleProductSubmit = async (data: LoanProductCreate) => {
    if (editingProduct) {
      await updateLoanProduct(token, editingProduct.id, data)
    } else {
      await createLoanProduct(token, data)
    }
    await loadData()
  }

  const handleFISubmit = async (data: FinancialInstitutionCreate) => {
    if (editingFI) {
      await updateFinancialInstitution(token, editingFI.id, data)
    } else {
      await createFinancialInstitution(token, data)
    }
    await loadData()
  }

  const handleToggleProduct = async (id: string) => {
    try {
      await toggleLoanProductStatus(token, id)
      await loadData()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to toggle product')
    }
  }

  const handleDeleteProduct = async (product: LoanProduct) => {
    if (!confirm(`Delete "${product.product_name}"? This cannot be undone.`)) return
    try {
      await deleteLoanProduct(token, product.id)
      await loadData()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Delete failed (admin only)')
    }
  }

  const handleToggleFI = async (id: string) => {
    try {
      await toggleFinancialInstitutionStatus(token, id)
      await loadData()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to toggle institution')
    }
  }

  const handleDeleteFI = async (fi: FinancialInstitution) => {
    if (!confirm(`Permanently delete "${fi.name}" and ALL its products? This cannot be undone.`)) return
    try {
      await deleteFinancialInstitution(token, fi.id)
      await loadData()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Delete failed (admin only)')
    }
  }

  const openAddProduct = () => { setEditingProduct(null); setShowProductForm(true) }
  const openEditProduct = (p: LoanProduct) => { setEditingProduct(p); setShowProductForm(true) }
  const openAddFI = () => { setEditingFI(null); setShowFIForm(true) }
  const openEditFI = (fi: FinancialInstitution) => { setEditingFI(fi); setShowFIForm(true) }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BrandLogo showTagline={false} size="sm" linkToHome />
            <div className="flex items-center gap-3">
              <Building2 className="w-7 h-7 text-teal-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Data Entry Portal</h1>
                <p className="text-xs text-gray-500">Logged in as: {user?.username || user?.full_name || 'dataoperator'}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:text-gray-900 hover:border-gray-400 transition-colors"
              >
                Admin Portal
              </button>
            )}
            <button
              onClick={() => { logout(); navigate('/') }}
              className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors text-sm"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
            <p className="text-sm text-gray-500 mb-1">Institutions</p>
            <p className="text-3xl font-bold text-gray-900">{institutions.length}</p>
            <p className="text-xs text-gray-400 mt-1">{institutions.filter(i => i.is_active).length} active</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
            <p className="text-sm text-gray-500 mb-1">Loan Products</p>
            <p className="text-3xl font-bold text-gray-900">{products.length}</p>
            <p className="text-xs text-gray-400 mt-1">{activeProducts.length} active</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-200">
            <p className="text-sm text-gray-500 mb-1">Updated This Week</p>
            <p className="text-3xl font-bold text-teal-600">{thisWeekActive.length}</p>
            <p className="text-xs text-gray-400 mt-1">active products</p>
          </div>
        </div>

        {/* Rate Scraper Panel */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-teal-600" />
                <h3 className="text-sm font-semibold text-gray-900">Rate Scraper</h3>
              </div>
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                scraperOnline === null ? 'bg-gray-100 text-gray-500' :
                scraperOnline ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
              }`}>
                {scraperOnline === null ? 'Checking...' : scraperOnline ? 'Online' : 'Offline'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {availableScrapers.length > 1 && (
                <select
                  value={selectedScraper}
                  onChange={(e) => setSelectedScraper(e.target.value)}
                  className="px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  disabled={scrapeStatus === 'scraping'}
                >
                  {availableScrapers.map((s) => (
                    <option key={s} value={s}>{s.toUpperCase()}</option>
                  ))}
                </select>
              )}
              <button
                onClick={handleTriggerScrape}
                disabled={scrapeStatus === 'scraping'}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  scrapeStatus === 'scraping'
                    ? 'bg-teal-100 text-teal-600 cursor-wait'
                    : 'bg-teal-600 text-white hover:bg-teal-700'
                }`}
              >
                <RefreshCw size={13} className={scrapeStatus === 'scraping' ? 'animate-spin' : ''} />
                {scrapeStatus === 'scraping' ? 'Scraping...' : 'Trigger Scrape'}
              </button>
            </div>
          </div>
          {/* Scraper options */}
          <div className="mt-3 flex items-center gap-5">
            <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={useSampleData}
                onChange={(e) => setUseSampleData(e.target.checked)}
                disabled={scrapeStatus === 'scraping'}
                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              Use Sample Data (Demo)
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={saveToDatabase}
                onChange={(e) => setSaveToDatabase(e.target.checked)}
                disabled={scrapeStatus === 'scraping'}
                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              Save to Database
            </label>
          </div>
          {/* Status line */}
          <div className="mt-2 text-xs text-gray-500">
            {scrapeStatus === 'scraping' && (
              <span className="text-teal-600 font-medium">Scraping in progress... this may take up to 60 seconds.</span>
            )}
            {scrapeStatus === 'done' && (
              <span className="text-green-700">{scrapeResult}</span>
            )}
            {scrapeStatus === 'error' && (
              <span className="text-red-600">{scrapeResult}</span>
            )}
            {scrapeStatus === 'idle' && lastScrapeTime && (
              <span>Last scrape: {new Date(lastScrapeTime).toLocaleString()}</span>
            )}
            {scrapeStatus === 'idle' && !lastScrapeTime && scraperOnline && (
              <span>No recent scrapes recorded.</span>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        {/* Financial Institutions Panel */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-gray-900">Financial Institutions</h2>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={loadData} className="text-gray-500 hover:text-gray-700 p-1" title="Refresh">
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={openAddFI}
                className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors"
              >
                <Plus size={16} />
                Add New
              </button>
            </div>
          </div>
          <div className="p-4">
            {institutions.length === 0 && !loading ? (
              <p className="text-center text-gray-500 py-6 text-sm">No institutions yet. Add one above.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {institutions.map((fi) => (
                  <div
                    key={fi.id}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
                      fi.is_active
                        ? 'bg-teal-50 border-teal-200 text-teal-800'
                        : 'bg-gray-50 border-gray-200 text-gray-500 line-through'
                    }`}
                  >
                    <span className="font-mono font-semibold">{fi.code}</span>
                    <span>{fi.name}</span>
                    <button
                      onClick={() => openEditFI(fi)}
                      className="text-gray-400 hover:text-teal-600 ml-1"
                      title="Edit"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => handleToggleFI(fi.id)}
                      className="text-gray-400 hover:text-amber-600"
                      title={fi.is_active ? 'Deactivate (cascades to products)' : 'Activate'}
                    >
                      {fi.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                    </button>
                    {isAdmin && !fi.is_active && (
                      <button
                        onClick={() => handleDeleteFI(fi)}
                        className="text-red-400 hover:text-red-600"
                        title="Delete institution + all products (admin only)"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Loan Products Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-lg font-semibold text-gray-900">Loan Products</h2>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Filters */}
              <select
                value={filterInstitution}
                onChange={(e) => setFilterInstitution(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Institutions</option>
                {institutions.map((fi) => (
                  <option key={fi.id} value={fi.id}>{fi.code} — {fi.name}</option>
                ))}
              </select>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                {Object.entries(PRODUCT_TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
              <select
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value as 'all' | 'active' | 'inactive')}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <button
                onClick={openAddProduct}
                className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus size={16} />
                Add Product
              </button>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-gray-300" />
              Loading...
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p>No products found{filterInstitution || filterType || filterActive !== 'all' ? ' for selected filters' : ''}.</p>
              <button onClick={openAddProduct} className="mt-3 text-blue-600 hover:underline text-sm">
                Add the first product
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Institution</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Comp</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">LVR</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tags</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Active</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredProducts.map((p) => (
                    <tr key={p.id} className={`hover:bg-gray-50 ${!p.is_active ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-teal-700 whitespace-nowrap">
                        {p.institution_code}
                      </td>
                      <td className="px-4 py-3 text-gray-900 max-w-[220px] truncate" title={p.product_name}>
                        {p.product_name}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {PRODUCT_TYPE_LABELS[p.product_type] ?? p.product_type}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">
                        {p.interest_rate?.toFixed(2)}%
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {p.comparison_rate?.toFixed(2)}%
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {p.max_lvr?.toFixed(0)}%
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          p.source === 'manual'
                            ? 'bg-blue-50 text-blue-700'
                            : 'bg-purple-50 text-purple-700'
                        }`}>
                          {p.source === 'manual' ? 'Manual' : 'Scraped'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {(() => {
                          const tagCount = Object.values(p.policy_tags || {}).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0)
                          return tagCount > 0
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">{tagCount} tags</span>
                            : <span className="text-xs text-gray-400">Untagged</span>
                        })()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleToggleProduct(p.id)}
                          className={p.is_active ? 'text-teal-600 hover:text-teal-700' : 'text-gray-400 hover:text-gray-600'}
                          title={p.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {p.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => openEditProduct(p)}
                            className="text-blue-500 hover:text-blue-700"
                            title="Edit"
                          >
                            <Pencil size={15} />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteProduct(p)}
                              className="text-red-400 hover:text-red-600"
                              title="Delete (admin only)"
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-500 text-right">
                {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
                {filteredProducts.length !== products.length ? ` (filtered from ${products.length})` : ''}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showProductForm && (
        <LoanProductForm
          product={editingProduct}
          institutions={institutions}
          onSubmit={handleProductSubmit}
          onClose={() => { setShowProductForm(false); setEditingProduct(null) }}
        />
      )}
      {showFIForm && (
        <FinancialInstitutionForm
          institution={editingFI}
          onSubmit={handleFISubmit}
          onClose={() => { setShowFIForm(false); setEditingFI(null) }}
        />
      )}
    </div>
  )
}
